/**
 * `prefers-reduced-motion`, read once and watched.
 *
 * Three things make this a module rather than a one-liner at each call site:
 *
 *  1. `matchMedia` DOES NOT EXIST IN JSDOM. Every read is guarded, and the
 *     absence answers `false` (motion allowed) — the same answer a browser that
 *     has never heard of the query gives. A crash here would take down the boot
 *     path of every screen.
 *  2. THE QUERY CHANGES WHILE THE APP IS OPEN. A player toggling the OS setting
 *     mid-session must see it take effect without a reload (§6 says so of the
 *     in-game settings, and it would be strange for the OS switch to be the one
 *     that needs one). `watchSystemMotion` is that subscription.
 *  3. `addEventListener` ON A MEDIA QUERY LIST IS NOT UNIVERSAL. Safari < 14
 *     only has the deprecated `addListener`. Both are tried, and the returned
 *     unsubscribe matches whichever was used.
 */

const QUERY = '(prefers-reduced-motion: reduce)';

/** A `MediaQueryList` as much of it as we use, so no `any` and no DOM-lib gap. */
interface MinimalMediaQueryList {
  readonly matches: boolean;
  addEventListener?(type: 'change', cb: () => void): void;
  removeEventListener?(type: 'change', cb: () => void): void;
  addListener?(cb: () => void): void;
  removeListener?(cb: () => void): void;
}

function queryList(): MinimalMediaQueryList | null {
  try {
    const mm = globalThis.matchMedia;
    if (typeof mm !== 'function') return null;
    return mm.call(globalThis, QUERY) as unknown as MinimalMediaQueryList;
  } catch {
    // A jsdom build with a stubbed `matchMedia` that throws, a sandboxed frame.
    return null;
  }
}

/** `true` when the OS asks for reduced motion. `false` when it cannot be asked. */
export function systemPrefersReducedMotion(): boolean {
  return queryList()?.matches === true;
}

/**
 * Call `onChange` whenever the OS preference flips. Returns the unsubscribe.
 * A no-op unsubscribe when the query is unavailable, so callers need no branch.
 */
export function watchSystemMotion(onChange: (reduced: boolean) => void): () => void {
  const list = queryList();
  if (list === null) return () => {};
  const handler = (): void => onChange(list.matches === true);
  if (typeof list.addEventListener === 'function') {
    list.addEventListener('change', handler);
    return () => list.removeEventListener?.('change', handler);
  }
  if (typeof list.addListener === 'function') {
    list.addListener(handler);
    return () => list.removeListener?.(handler);
  }
  return () => {};
}
